import React from 'react';
import Select, { FormatOptionLabelMeta } from 'react-select';
import { ActionState } from 'services/EpdClient';
import { useTheme } from 'styled-components';
import { Theme } from 'styles/theme';
import { Option } from 'types/types';
import SelectWithCustomOptionsFilter, { CustomOptionsFilter } from 'components/form/SelectWithCustomOptionsFilter';
import useIsReadOnlyMode from '../compiler/hooks/useIsReadOnlyMode';
import SmartLabel, { TSmartLabelProps } from '../labels/SmartLabel';
import { ClearIndicator, DropdownIndicator } from './common-icons';
import { getCustomWizardSelectStyles } from './select-styles';
import { ReactComponent as CheckIconComponent } from 'images/v2/icons/svg/fill/svg/check.svg';

type TProps = Partial<TSmartLabelProps> & {
  name: string;
  value?: any;
  options: any[] | undefined;
  isMulti?: boolean;
  style?: any;
  containerClassName?: string;
  containerStyle?: any;
  onChanged?(propertyName: string, newValue: any, valueOfOptionOrHonestlyNoIdeaWhatThisIsFor?: any): void;
  disabled?: boolean;
  isClearable?: boolean;
  overrideReadOnlyMode?: boolean;
  error?: any;
  state?: Record<string, ActionState>;
  isOptionDisabled?: (option: any, selectValue: any[]) => boolean;
  filterOption?: any;
  customOptionsFilter?: CustomOptionsFilter;
  [key: string]: any;
};

const CompilerSelectControlledComponent: React.FunctionComponent<TProps> = ({
  label,
  options,
  tooltip,
  tooltipPosition,
  labelStyle,
  containerClassName,
  containerStyle,
  name,
  value,
  onChanged,
  disabled,
  isOptionDisabled,
  style,
  required,
  isMulti,
  isClearable = true,
  overrideReadOnlyMode,
  error,
  state,
  ...props
}) => {
  const theme = useTheme() as Theme;
  const customSelectStyles = getCustomWizardSelectStyles(theme);
  const isReadOnly = (useIsReadOnlyMode() && !overrideReadOnlyMode) || disabled;

  const selectProps = {
    components: { ClearIndicator, DropdownIndicator },
    options: options,
    name: name,
    value: value,
    onChange: (e: any) => {
      if (!onChanged) {
        return;
      }
      const newValue = isMulti ? e?.map((x: Option) => x.value) : e?.value;
      if (onChanged.length === 2) {
        onChanged(name, newValue);
      } else {
        onChanged(name, newValue, value?.value);
      }
    },
    isClearable: isClearable,
    isMulti: isMulti,
    isDisabled: isReadOnly,
    isOptionDisabled: isOptionDisabled as any,
    menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
    menuPosition: 'fixed',
    styles: customSelectStyles,
    formatOptionLabel: (option: any, meta: FormatOptionLabelMeta<any>) => {
      const { context, selectValue } = meta;

      if (context === 'menu') {
        const isSelected = Array.isArray(selectValue) && selectValue.some((selected) => selected.value === option.value);

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{option.label}</span>
            {isSelected && <CheckIconComponent />}
          </div>
        );
      }

      return option.label;
    },
    ...props,
  };
  const SelectComponent = selectProps.customOptionsFilter ? SelectWithCustomOptionsFilter : Select;

  return (
    <div
      className={containerClassName}
      style={{
        ...containerStyle,
        width: '100%',
        display: state?.[name] === ActionState.Hidden ? 'none' : 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <SmartLabel
          label={label}
          required={required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={
            isMulti ? value && value.length > 0 : value?.value !== undefined && value?.value !== null && value?.value !== ''
          }
          error={error}
        />
      )}
      <SelectComponent {...(selectProps as any)} />
    </div>
  );
};

export default CompilerSelectControlledComponent;
